import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const EMPTY = {
  name: "", partner_type: "Bank", contact_person: "", contact_email: "", contact_phone: "",
  default_split_pct: 80, min_cibil: 700, max_foir: 0.5, max_ltv: 0.75, roi_min: 11, roi_max: 16,
  eligible_grades: "A,B", max_vehicle_age: 8, max_vehicle_km: 120000,
  min_ticket_size: 200000, max_ticket_size: 2000000, status: "active", notes: "",
};

export default function PartnerRulesTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/policy/partners").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Partner name is required"); return; }
    setBusy(true);
    try {
      await api.post("/policy/partners", {
        ...form,
        default_split_pct: Number(form.default_split_pct),
        min_cibil: Number(form.min_cibil), max_foir: Number(form.max_foir), max_ltv: Number(form.max_ltv),
        roi_min: Number(form.roi_min), roi_max: Number(form.roi_max),
        eligible_grades: String(form.eligible_grades).split(",").map(s => s.trim()).filter(Boolean),
        max_vehicle_age: Number(form.max_vehicle_age), max_vehicle_km: Number(form.max_vehicle_km),
        min_ticket_size: Number(form.min_ticket_size), max_ticket_size: Number(form.max_ticket_size),
      });
      toast.success("Partner policy created");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="partner-rules-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-600">Bank / NBFC / investor specific credit rules used for co-lending routing & sell-down eligibility.</p>
        <Button onClick={() => setOpen(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="partner-add-btn"><Plus size={16} className="mr-1" /> Add Partner Rules</Button>
      </div>
      <Card className="border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Partner</th><th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Split</th><th className="text-left px-4 py-3">Min CIBIL</th>
              <th className="text-left px-4 py-3">Max FOIR</th><th className="text-left px-4 py-3">Max LTV</th>
              <th className="text-left px-4 py-3">ROI range</th><th className="text-left px-4 py-3">Grades</th>
              <th className="text-left px-4 py-3">Ticket size</th><th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`partner-row-${p.name}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-3">{p.partner_type}</td>
                <td className="px-4 py-3 font-mono">{p.default_split_pct}%</td>
                <td className="px-4 py-3 font-mono">{p.min_cibil}</td>
                <td className="px-4 py-3 font-mono">{Math.round(p.max_foir * 100)}%</td>
                <td className="px-4 py-3 font-mono">{Math.round(p.max_ltv * 100)}%</td>
                <td className="px-4 py-3 font-mono">{p.roi_min}–{p.roi_max}%</td>
                <td className="px-4 py-3">{(p.eligible_grades || []).join(", ")}</td>
                <td className="px-4 py-3 font-mono text-xs">{formatINR(p.min_ticket_size)}–{formatINR(p.max_ticket_size)}</td>
                <td className="px-4 py-3 capitalize">{p.status}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-slate-500">No partner rules yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="partner-dialog">
          <DialogHeader><DialogTitle>Partner bank / NBFC credit rules</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="col-span-2"><Label className="text-xs">Partner name</Label><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="partner-name" /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.partner_type} onValueChange={v => set("partner_type", v)}>
                <SelectTrigger data-testid="partner-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bank">Bank</SelectItem><SelectItem value="NBFC">NBFC</SelectItem>
                  <SelectItem value="Investor">Investor</SelectItem><SelectItem value="Fintech">Fintech</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Contact person</Label><Input value={form.contact_person} onChange={e => set("contact_person", e.target.value)} /></div>
            <div><Label className="text-xs">Contact email</Label><Input value={form.contact_email} onChange={e => set("contact_email", e.target.value)} /></div>
            <div><Label className="text-xs">Contact phone</Label><Input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} /></div>
            <div><Label className="text-xs">Partner split %</Label><Input type="number" value={form.default_split_pct} onChange={e => set("default_split_pct", e.target.value)} data-testid="partner-split" /></div>
            <div><Label className="text-xs">Min CIBIL</Label><Input type="number" value={form.min_cibil} onChange={e => set("min_cibil", e.target.value)} /></div>
            <div><Label className="text-xs">Max FOIR (0–1)</Label><Input type="number" step={0.01} value={form.max_foir} onChange={e => set("max_foir", e.target.value)} /></div>
            <div><Label className="text-xs">Max LTV (0–1)</Label><Input type="number" step={0.01} value={form.max_ltv} onChange={e => set("max_ltv", e.target.value)} /></div>
            <div><Label className="text-xs">ROI min %</Label><Input type="number" step={0.1} value={form.roi_min} onChange={e => set("roi_min", e.target.value)} /></div>
            <div><Label className="text-xs">ROI max %</Label><Input type="number" step={0.1} value={form.roi_max} onChange={e => set("roi_max", e.target.value)} /></div>
            <div><Label className="text-xs">Eligible grades (csv)</Label><Input value={form.eligible_grades} onChange={e => set("eligible_grades", e.target.value)} placeholder="A,B" /></div>
            <div><Label className="text-xs">Max vehicle age (y)</Label><Input type="number" value={form.max_vehicle_age} onChange={e => set("max_vehicle_age", e.target.value)} /></div>
            <div><Label className="text-xs">Max vehicle km</Label><Input type="number" value={form.max_vehicle_km} onChange={e => set("max_vehicle_km", e.target.value)} /></div>
            <div><Label className="text-xs">Min ticket (₹)</Label><Input type="number" value={form.min_ticket_size} onChange={e => set("min_ticket_size", e.target.value)} /></div>
            <div><Label className="text-xs">Max ticket (₹)</Label><Input type="number" value={form.max_ticket_size} onChange={e => set("max_ticket_size", e.target.value)} /></div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="partner-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
